export enum VoEStatus {
  NOT_STARTED = 'NOT_STARTED',
  REQUESTED = 'REQUESTED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  CANCELED = 'CANCELED',
  DECLINED = 'DECLINED',
}

export enum VoEType {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum VoEAuditTrailType {
  CALL = 'CALL',
  EMAIL = 'EMAIL',
  FAX = 'FAX',
  NOTE = 'NOTE',
  ATS = 'ATS',
  RESPONSE_RECEIVED = 'RESPONSE_RECEIVED',
}

export enum VoECompletionMethod {
  DOUBLE_NICKEL_FORM = 'DOUBLE_NICKEL_FORM',
  PHONE = 'PHONE',
  FAX = 'FAX',
  EMAIL_EXTERNAL = 'EMAIL_EXTERNAL',
  ATS_PLATFORM = 'ATS_PLATFORM',
  NO_RESPONSE = 'NO_RESPONSE',
  UNKNOWN = 'UNKNOWN',
}

export enum EmployerClassification {
  NON_CARRIER_UNKNOWN = 'NON_CARRIER_UNKNOWN',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  EMPLOYMENT_GAP = 'EMPLOYMENT_GAP',
  MILITARY_SERVICE = 'MILITARY_SERVICE',
  GIG = 'GIG',
}

/** When the company's automated outreach chain starts. Mirrors Prisma `VoEOutreachTrigger`. */
export enum VoEOutreachTrigger {
  IMMEDIATELY = 'IMMEDIATELY',
  AFTER_MANUAL = 'AFTER_MANUAL',
  AUTOMATION_ONLY = 'AUTOMATION_ONLY',
}

/** A step of the chain. Mirrors Prisma `VoEOutreachType`. */
export enum VoEOutreachType {
  EMAIL = 'EMAIL',
  CALL = 'CALL',
  FAX = 'FAX',
}

/** Where an outreach action is in its life. Mirrors Prisma `VoEActionStatus`. */
export enum VoEActionStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED',
}

/**
 * Who reached the employer before the answer arrived. Stamped once at the
 * first real response and never changed after. Mirrors Prisma
 * `VoEResponseAutonomy`; null until the employer answers.
 */
export enum VoEResponseAutonomy {
  AUTONOMOUS = 'AUTONOMOUS',
  ASSISTED = 'ASSISTED',
  MANUAL = 'MANUAL',
  NO_OUTREACH = 'NO_OUTREACH',
}

export const VoEResponseAutonomyLabels: Record<
  VoEResponseAutonomy,
  { label: string; tooltip: string }
> = {
  [VoEResponseAutonomy.AUTONOMOUS]: {
    label: 'Auto',
    tooltip: 'Automation did all the work, with no employee',
  },
  [VoEResponseAutonomy.ASSISTED]: {
    label: 'Assisted',
    tooltip: 'Automation and an employee both worked on it',
  },
  [VoEResponseAutonomy.MANUAL]: {
    label: 'Manual',
    tooltip: 'An employee did all the work, with no automation',
  },
  [VoEResponseAutonomy.NO_OUTREACH]: {
    label: 'None',
    tooltip: 'No outreach was logged',
  },
}

/** Mirrors Prisma's `VoENextActionOwner`; null once the VoE is closed. */
export enum VoENextActionOwner {
  AUTOMATED = 'AUTOMATED',
  MANUAL = 'MANUAL',
}

/** Mirrors Prisma's `VoENextActionReason`; null while automation holds it. */
export enum VoENextActionReason {
  PLATFORM = 'PLATFORM',
  FAX = 'FAX',
  EMAILS_EXHAUSTED = 'EMAILS_EXHAUSTED',
  CALLS_EXHAUSTED = 'CALLS_EXHAUSTED',
  WRONG_NUMBER = 'WRONG_NUMBER',
  NO_CONTACT_INFO = 'NO_CONTACT_INFO',
  DO_NOT_CONTACT = 'DO_NOT_CONTACT',
  NOT_A_CARRIER = 'NOT_A_CARRIER',
  CURRENT_EMPLOYER = 'CURRENT_EMPLOYER',
  REVIEW_RESPONSE = 'REVIEW_RESPONSE',
}

export const VoENextActionOwnerLabels: Record<
  VoENextActionOwner,
  { label: string; tooltip: string }
> = {
  [VoENextActionOwner.AUTOMATED]: {
    label: 'Scheduled',
    tooltip: 'Automation has the next email or call scheduled',
  },
  [VoENextActionOwner.MANUAL]: {
    label: 'Manual',
    tooltip: 'Automation has stopped or cannot start; the next step is yours',
  },
}

export const VoENextActionReasonLabels: Record<
  VoENextActionReason,
  { label: string; tooltip: string }
> = {
  [VoENextActionReason.PLATFORM]: {
    label: 'Platform',
    tooltip: 'This employer answers verifications only through a platform',
  },
  [VoENextActionReason.FAX]: {
    label: 'Fax',
    tooltip: 'This employer takes verifications by fax',
  },
  [VoENextActionReason.EMAILS_EXHAUSTED]: {
    label: 'No email reply',
    tooltip: 'Every automated email went out and nothing came back',
  },
  [VoENextActionReason.CALLS_EXHAUSTED]: {
    label: 'No answer',
    tooltip: 'Automated calling stopped without a verification',
  },
  [VoENextActionReason.WRONG_NUMBER]: {
    label: 'Wrong number',
    tooltip: 'The voice agent reached the wrong company',
  },
  [VoENextActionReason.NO_CONTACT_INFO]: {
    label: 'No contact',
    tooltip: 'Nothing automation can email or call',
  },
  [VoENextActionReason.DO_NOT_CONTACT]: {
    label: 'Do not contact',
    tooltip: 'The applicant withheld consent to contact this employer',
  },
  [VoENextActionReason.NOT_A_CARRIER]: {
    label: 'Not a carrier',
    tooltip: 'Self-employment, a gap, military service, or gig work',
  },
  [VoENextActionReason.CURRENT_EMPLOYER]: {
    label: 'Current employer',
    tooltip: 'Your settings leave current employers to a person',
  },
  [VoENextActionReason.REVIEW_RESPONSE]: {
    label: 'Review response',
    tooltip: 'The employer answered; the response is waiting for review',
  },
}
